import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Settings, Plus, Trash2, Pencil, Check, X, Info } from 'lucide-react';
import { toast } from 'sonner';

export const NO_COMMERCIAL_LABEL = '__none__';

export default function CommercialLabelSelect({ value, onChange, canManage = false, showHeader = true }) {
  const [labels, setLabels]             = useState([]);
  const [manageOpen, setManageOpen]     = useState(false);
  const [newLabel, setNewLabel]         = useState('');
  const [editingId, setEditingId]       = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [saving, setSaving]             = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/commercial-labels');
      setLabels(res.data || []);
    } catch {
      setLabels([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      const res = await api.post('/commercial-labels', { label });
      setLabels(prev => prev.find(l => l.id === res.data.id) ? prev : [...prev, res.data]);
      setNewLabel('');
      toast.success('Label added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add label');
    } finally { setSaving(false); }
  };

  const handleRename = async (labelId) => {
    const label = editingLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      const res = await api.put(`/commercial-labels/${labelId}`, { label });
      setLabels(prev => prev.map(l => l.id === labelId ? res.data : l));
      setEditingId(null); setEditingLabel('');
      toast.success('Label renamed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename label');
    } finally { setSaving(false); }
  };

  const handleDelete = async (labelId) => {
    try {
      await api.delete(`/commercial-labels/${labelId}`);
      setLabels(prev => prev.filter(l => l.id !== labelId));
      if (value === labelId) onChange(null);
      toast.success('Label removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove label');
    }
  };

  const label = 'text-sm text-[#6E6258] font-medium mb-1.5 block';

  return (
    <>
      {showHeader && (
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-base text-foreground font-outfit font-semibold flex items-center gap-1.5">
            Commercial
            <span title="Shown next to the byline on the published story">
              <Info size={13} className="text-[#C9B49A]" />
            </span>
          </span>
          {canManage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setManageOpen(true)}
              className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8] h-7 px-2"
            >
              <Settings size={14} className="mr-1" />Labels
            </Button>
          )}
        </div>
      )}

      <label className={label}>
        Commercial Label <span className="text-[#9C8E84] font-normal">(optional)</span>
      </label>
      <Select
        value={value || NO_COMMERCIAL_LABEL}
        onValueChange={v => onChange(v === NO_COMMERCIAL_LABEL ? null : v)}
      >
        <SelectTrigger className="bg-white text-[#1F1A17] border-border" data-testid="commercial-label-select">
          <SelectValue placeholder="Select commercial label" />
        </SelectTrigger>
        <SelectContent className="bg-white border-border">
          <SelectItem value={NO_COMMERCIAL_LABEL} className="text-[#9C8E84]">None</SelectItem>
          {labels.map(l => (
            <SelectItem key={l.id} value={l.id} className="text-[#1F1A17]">{l.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Manage Commercial Labels</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add, rename or remove the labels available on stories. Removing one leaves published stories unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                placeholder="New label name"
                className="bg-white text-[#1F1A17] border-border flex-1"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
              />
              <Button
                onClick={handleAdd}
                disabled={!newLabel.trim() || saving}
                className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
              >
                <Plus size={16} />
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {labels.map(l => (
                <div key={l.id} className="flex items-center justify-between gap-2 p-2 bg-[#FFF4E8] rounded">
                  {editingId === l.id ? (
                    <>
                      <Input
                        value={editingLabel}
                        onChange={e => setEditingLabel(e.target.value)}
                        className="bg-white text-[#1F1A17] border-border h-7 text-sm flex-1"
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleRename(l.id))}
                        autoFocus
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleRename(l.id)} disabled={saving}
                        className="text-emerald-600 hover:bg-emerald-50 h-7 w-7 p-0">
                        <Check size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingId(null); setEditingLabel(''); }}
                        className="text-muted-foreground hover:text-foreground h-7 w-7 p-0">
                        <X size={14} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-foreground flex-1 truncate">{l.label}</span>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingId(l.id); setEditingLabel(l.label); }}
                        className="text-[#6E6258] hover:text-foreground hover:bg-[#FFE4C4] h-7 w-7 p-0">
                        <Pencil size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(l.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0">
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                </div>
              ))}
              {labels.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No commercial labels yet</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManageOpen(false)} className="text-muted-foreground hover:text-foreground">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}